import { colorGunmetal, colorWhite, rebrand } from '../../../../theme/color';
import { cssRaw, style } from 'typestyle';
import { important, px } from 'csx';
import { spacing } from '../../../../theme/spacing';
import { desktopBreakpoint, fontBody, fontstackHeading } from '../../config';
import { center, centerCenter, centerJustified, flexRoot, vertical, wrap } from 'csstips';
import { mediaDesktop, mediaMediumDesktop, mediaUpToDesktop, mediaWideDesktop } from '../../utilities/mediaQueries';
import { shadowElevation5, shadowElevationPrimary } from '../../../../theme/shadow';
import { defaultHeadingStyles } from '../../utilities/font';
import { borderRadius } from '../../../../theme/border';

// Used for container queries
const planCardContainerName = 'PlanCard';

/**
 * Styles for plans V2 content
 */
export const styles = {
  main: style(),
  infoBlock: style({
    textAlign: 'center',
    $nest: {
      '& > h1': {
        margin: `0 0 ${spacing.XLARGE}`,
      },
      '& > p': {
        margin: 0,
      },
    },
  }),
  introText: style({
    maxWidth: px(desktopBreakpoint),
    margin: '0 auto',
    $nest: {
      '& p': {
        margin: 0,
        fontSize: px(20),
      },
      '& p + p': {
        marginTop: spacing.XLARGE,
      },
    },
  }),
  loading: style({
    height: px(400),
  }),
  plans: style(
    flexRoot,
    centerJustified,
    wrap,
    {
      margin: `${spacing.XXXLARGE} auto 0`,
      gap: `${spacing.XLARGE} ${spacing.DEFAULT}`,
    },
    mediaWideDesktop({
      flexWrap: 'nowrap',
      '-webkit-flex-wrap': 'nowrap',
    })
  ),
  plansNoChannel: style(
    {
      position: 'relative',
    },
    mediaDesktop({
      height: px(150),
      overflow: 'hidden',
      $nest: {
        '&::after': {
          content: "''",
          position: 'absolute',
          top: px(44),
          right: 0,
          bottom: 0,
          left: 0,
          background: 'linear-gradient(to bottom, transparent, #F3F5F7)',
          zIndex: 1,
        },
        '&.fade-to-white::after': {
          background: 'linear-gradient(to bottom, transparent, #FFF)',
        },
      },
    })
  ),
  planWrapper: style(
    {
      containerType: 'inline-size',
      containerName: planCardContainerName,
      width: '100%',
      maxWidth: px(360),
      minWidth: px(280),
      padding: important(0),
      $nest: {
        '& > div': {
          position: 'relative',
          height: '100%',
          backgroundColor: colorWhite.toHexString(),
          color: 'inherit',
          padding: spacing.LARGE,
          overflow: 'visible',
          ...vertical,
          ...shadowElevation5,
        },
        '&.plan-backstage > div': {
          ...shadowElevationPrimary,
        },
        '& h2': {
          fontSize: '48px',
          fontFamily: fontstackHeading,
          color: rebrand.primary[100].toString(),
          margin: 0,
          padding: 0,
        },
        '&.enterprise-plan h2': {
          color: 'inherit',
        },
        '&.plan-trial h2': {
          color: 'inherit',
        },
        '&.plan-launchpad h2': {
          color: rebrand.neutralOnDark[70].toString(),
        },
      },
    },
    mediaMediumDesktop({
      $nest: {
        '& > div': {
          padding: spacing.XLARGE,
        },
      },
    })
  ),
  planWrapperNoChannel: style(
    {
      color: important(rebrand.neutralOnDark[20].toString()),
      height: 'fit-content',
      $nest: {
        '& h2': {
          color: important(rebrand.neutralOnDark[20].toString()),
        },
        '& > div *': {
          color: important(rebrand.neutralOnDark[20].toString()),
        },
      },
    },
    mediaUpToDesktop({
      display: 'none',
    })
  ),
  planPriceWrapper: style(flexRoot, vertical, centerJustified, {
    margin: `${spacing.LARGE} 0 0`,
    minHeight: px(72),
  }),
  pricingContentsSubscription: style({
    ...defaultHeadingStyles,
    fontSize: px(28),
    whiteSpace: 'pre-wrap',
    margin: 0,
  }),
  pricingContentXLarge: style({
    fontSize: px(64),
  }),
  pricingContentMonth: style({
    color: rebrand.neutralOnDark[70].toString(),
  }),
  pricingContentFree: style({
    fontSize: px(40),
  }),
  pricingContentLarge: style({
    fontSize: px(48),
  }),
  pricingContentFooter: style({
    fontSize: px(12),
    fontWeight: 700,
  }),
  pricingStrikeThrough: style({
    position: 'relative',
    display: 'inline-block',
    $nest: {
      '&::after': {
        content: "''",
        display: 'block',
        position: 'absolute',
        top: 'calc(50% - 3px)',
        left: px(-8),
        right: px(-8),
        height: px(6),
        transform: 'rotate(-9deg)',
        backgroundColor: rebrand.primary[50].toHexString(),
      },
    },
  }),
  planHeadingInfoText: style(flexRoot, center, {
    fontSize: px(20),
    fontWeight: 700,
    fontFamily: fontBody,
    lineHeight: '28px',
    marginTop: spacing.LARGE,
    color: rebrand.primary[100].toString(),
    $nest: {
      '& svg': {
        flexShrink: 0,
        marginRight: spacing.XXSMALL,
      },
      '.enterprise-plan &': {
        fontWeight: 400,
        color: rebrand.contrast[40].toString(),
      },
      '.plan-trial &': {
        color: 'inherit',
      },
      '.plan-launchpad &': {
        color: rebrand.neutralOnDark[70].toString(),
      },
    },
  }),
  padlock: style(
    {
      position: 'absolute',
      top: spacing.LARGE,
      right: spacing.LARGE,
    },
    mediaMediumDesktop({
      top: spacing.XLARGE,
      right: spacing.XLARGE,
    })
  ),
  iconList: style({
    flexGrow: 1,
    margin: `${spacing.LARGE} 0 0`,
  }),
  planButtonWrapper: style({
    marginTop: spacing.LARGE,
  }),
  postDiscountPriceText: style({
    margin: `0 0 ${spacing.XSMALL}`,
    fontSize: px(12),
    fontWeight: 700,
    textAlign: 'center',
  }),
  planButton: style(flexRoot, centerCenter, {
    width: '100%',
    $nest: {
      '& > span': {
        marginLeft: 0,
      },
    },
  }),
  planButtonLink: style(flexRoot, centerCenter),
  planButtonIcon: style({
    marginLeft: spacing.XSMALL,
  }),
  smallText: style({
    fontSize: px(12),
    color: rebrand.contrast[40].toString(),
    textAlign: 'center',
    margin: `${spacing.XSMALL} 0 0`,
  }),
  greenText: style({
    color: rebrand.secondary[70].toString(),
  }),
  warningText: style({
    color: rebrand.semantic.warning[60].toString(),
  }),
  channelInfoBox: style(flexRoot, {
    position: 'relative',
    backgroundColor: rebrand.contrast[10].toString(),
    color: rebrand.contrast[40].toString(),
    borderRadius: borderRadius.MEDIUM,
    padding: spacing.DEFAULT,
    gap: spacing.XSMALL,
    maxWidth: px(685),
    margin: `${spacing.XLARGE} auto 0`,
    $nest: {
      '&::before': {
        content: "''",
        position: 'absolute',
        backgroundColor: rebrand.contrast[10].toString(),
        width: px(14),
        height: px(14),
        top: px(-7),
        left: '50%',
        transform: 'rotate(45deg)',
        zIndex: 1,
      },
      '& svg': {
        flexShrink: 0,
      },
      '& p': {
        margin: 0,
        fontSize: px(12),
        $nest: {
          '& strong': {
            color: rebrand.contrast[50].toString(),
          },
        },
      },
    },
  }),
  info: style({
    textAlign: 'center',
    fontSize: px(12),
    maxWidth: px(desktopBreakpoint),
    margin: `${spacing.XXXLARGE} auto 0`,
    $nest: {
      '& a': {
        textDecoration: 'underline',
      },
    },
  }),
  subscribeError: style({
    textAlign: 'center',
    color: 'red',
    maxWidth: px(desktopBreakpoint),
    margin: `${spacing.XXXLARGE} auto 0`,
    $nest: {
      '& a': {
        textDecoration: 'underline',
      },
    },
  }),
  faqs: style({
    maxWidth: px(desktopBreakpoint),
    margin: `${spacing.XXXLARGE} auto 0`,
  }),
};

// Container queries aren't included in typestyle, so we have to chuck this in raw CSS
// If you're not familiar with container queries, see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_containment/Container_queries
cssRaw(`
  @container ${planCardContainerName} (max-width: 320px) {
    .${styles.planHeadingInfoText} {
      font-size: 16px !important;
    }
    .${styles.pricingContentLarge} {
      font-size: 40px !important;
    }
    .${styles.pricingContentXLarge} {
      font-size: 48px !important;
    }
  }
`);
